import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { setMetaFailed, setMetaStarted, setMetaSucceeded } from '@mews-distributor/common';
import { keyBy } from '@mews/fp';

import { Metadata } from '../../../types/Metadata';
import { ProductPrice, ProductPricing } from './types';

interface ProductPricingResponse {
    productPricingHash: string;
    prices: Array<ProductPrice>;
}

const keyByProductId = keyBy('productId');

const dataInitialState: ProductPricing['data'] = {};

const productPricingDataSlice = createSlice({
    name: 'productPricingData',
    initialState: dataInitialState,
    reducers: {
        setProductPricing: (
            state,
            { payload: { prices, productPricingHash } }: PayloadAction<ProductPricingResponse>
        ) => ({
            ...state,
            [productPricingHash]: keyByProductId(prices),
        }),
    },
});

const updateProductPricingMeta =
    <S>(metaHandler: () => Metadata) =>
    (state: S, { payload: { productPricingHash } }: PayloadAction<Omit<ProductPricingResponse, 'prices'>>) => ({
        ...state,
        [productPricingHash]: metaHandler(),
    });

const metaInitialState: ProductPricing['meta'] = {};

const productPricingMetaSlice = createSlice({
    name: 'productPricingMeta',
    initialState: metaInitialState,
    reducers: {
        fetchProductPricingStarted: updateProductPricingMeta(setMetaStarted),
        fetchProductPricingSucceeded: updateProductPricingMeta(setMetaSucceeded),
        fetchProductPricingFailed: updateProductPricingMeta(setMetaFailed),
    },
});

export const productPricingReducer = combineReducers({
    data: productPricingDataSlice.reducer,
    meta: productPricingMetaSlice.reducer,
});

const { setProductPricing } = productPricingDataSlice.actions;
const { fetchProductPricingStarted, fetchProductPricingSucceeded, fetchProductPricingFailed } =
    productPricingMetaSlice.actions;

export { setProductPricing, fetchProductPricingStarted, fetchProductPricingSucceeded, fetchProductPricingFailed };
